import apiClient from './axios';
import type {
  User,
  UserCreateInput,
  UserUpdateInput,
  UserListParams,
  InviteInput,
  PaginatedListResponse,
} from '@cms/models';

export type UsersListParams = UserListParams;
export type UsersListResponse = PaginatedListResponse<User>;

export const userApi = {
  getAll: async (): Promise<User[]> => {
    const response = await apiClient.get<User[]>(`/users`);
    return response.data;
  },

  getPaginated: async (params: UsersListParams = {}): Promise<UsersListResponse> => {
    const { page = 1, pageSize = 25, search, status, sortBy, sortOrder, platformUsersOnly } =
      params;
    const response = await apiClient.get<UsersListResponse>('/users', {
      params: {
        page,
        pageSize,
        ...(search ? { search } : {}),
        ...(status ? { status } : {}),
        ...(sortBy ? { sortBy } : {}),
        ...(sortOrder ? { sortOrder } : {}),
        ...(platformUsersOnly ? { platformUsersOnly: 'true' } : {}),
      },
    });
    return response.data;
  },

  getById: async (id: string): Promise<User> => {
    const response = await apiClient.get<User>(`/users/${id}`);
    return response.data;
  },

  create: async (data: UserCreateInput): Promise<User> => {
    const response = await apiClient.post<User>(`/users`, data);
    return response.data;
  },

  update: async (id: string, data: UserUpdateInput): Promise<User> => {
    const response = await apiClient.patch<User>(`/users/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/users/${id}`);
  },

  invite: async (data: InviteInput): Promise<{ sent: boolean }> => {
    const response = await apiClient.post<{ sent: boolean }>(`/users/invite`, data);
    return response.data;
  },

  assignRoles: async (id: string, roleIds: string[]): Promise<User> => {
    const response = await apiClient.post<User>(`/users/${id}/roles`, {
      roleIds,
    });
    return response.data;
  },

  getMachineIds: async (id: string): Promise<string[]> => {
    const response = await apiClient.get<string[]>(`/users/${id}/machines`);
    return response.data;
  },

  addMachineId: async (id: string, machineId: string): Promise<string[]> => {
    const response = await apiClient.post<string[]>(`/users/${id}/machines`, {
      machineId,
    });
    return response.data;
  },

  removeMachineId: async (id: string, machineId: string): Promise<string[]> => {
    const encoded = encodeURIComponent(machineId);
    const response = await apiClient.delete<string[]>(`/users/${id}/machines/${encoded}`);
    return response.data;
  },
};
